import {ArrowDown, ArrowUp, ArrowUpDown, History, Users} from 'lucide-react'
import type {UserLogReport} from './types'
import {formatLogDate} from './logUtils'

const numberFormat = new Intl.NumberFormat('pt-BR')
const percentageFormat = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 1})

interface LogsReportProps {
  data: UserLogReport[]
  totalLogs?: number
  onUserSelect: (id: number | null) => void
  sortField: string
  sortDirection: 'asc' | 'desc'
  onSort: (field: string) => void
}

export default function LogsReport({
  data,
  totalLogs,
  onUserSelect,
  sortField,
  sortDirection,
  onSort,
}: LogsReportProps) {
  const hasTotal = typeof totalLogs === 'number' && Number.isFinite(totalLogs) && totalLogs >= 0
  const sortableHeader = (field: string, label: string, className = '') => {
    const selected = sortField === field
    const Icon = selected ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
    return (
      <th
        scope='col'
        className={className}
        aria-sort={selected ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
      >
        <button
          type='button'
          className={`logs-sort-button${selected ? ' is-active' : ''}`}
          onClick={() => onSort(field)}
        >
          <Icon size={14} aria-hidden='true' />
          {label}
        </button>
      </th>
    )
  }

  if (!data.length) {
    return (
      <div className='logs-empty-state'>
        <Users size={36} aria-hidden='true' />
        <strong>Nenhuma atividade por usuário</strong>
        <span>Experimente ampliar o período ou ajustar os filtros.</span>
      </div>
    )
  }

  return (
    <section className='logs-report' aria-label='Relatório de atividades por usuário'>
      <p className='logs-report-note logs-muted'>
        Atividades registradas nos filtros atuais.{' '}
        {hasTotal
          ? `A participação considera o total de ${numberFormat.format(totalLogs)} registros.`
          : 'Participação disponível após carregar os indicadores.'}
      </p>
      <div className='logs-table-wrap'>
        <table className='logs-report-table'>
          <caption className='visually-hidden'>
            Contagem de atividades registradas por usuário e acesso à linha do tempo.
          </caption>
          <colgroup>
            <col className='logs-report-col-user' />
            <col className='logs-report-col-count' />
            <col className='logs-report-col-types' />
            <col className='logs-report-col-days' />
            <col className='logs-report-col-dates' />
            <col className='logs-report-col-activity' />
            <col className='logs-report-col-action' />
          </colgroup>
          <thead>
            <tr>
              {sortableHeader('user', 'Usuário')}
              {sortableHeader('log_count', 'Registros', 'logs-numeric-cell')}
              {sortableHeader('unique_types', 'Tipos', 'logs-numeric-cell')}
              {sortableHeader('active_days', 'Dias ativos', 'logs-numeric-cell')}
              {sortableHeader('last_activity', 'Última atividade')}
              <th scope='col'>Tipos mais registrados</th>
              <th scope='col'>
                <span className='visually-hidden'>Explorar usuário</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((user) => {
              const name =
                user.user?.trim() ||
                (user.user_id == null ? 'Usuário não identificado' : `Usuário #${user.user_id}`)
              const share =
                hasTotal && totalLogs > 0
                  ? Math.min(100, Math.max(0, (user.log_count / totalLogs) * 100))
                  : 0
              const shareLabel =
                share > 0 && share < 0.1 ? '< 0,1%' : `${percentageFormat.format(share)}%`
              const topTypes = [...user.typeDistribution].sort((a, b) => b.count - a.count)
              return (
                <tr key={user.user_id == null ? 'unknown-user' : user.user_id}>
                  <td>
                    <div className='logs-user-identity'>
                      <span className='logs-user-avatar' aria-hidden='true'>
                        {name.charAt(0).toLocaleUpperCase('pt-BR')}
                      </span>
                      <div className='logs-user-identity-text'>
                        <strong title={name}>{name}</strong>
                        <span className='logs-muted'>
                          {user.user_id == null ? 'Sem identificação' : `ID ${user.user_id}`}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className='logs-numeric-cell logs-report-count-cell'>
                    <strong className='logs-report-count'>
                      {numberFormat.format(user.log_count)}
                    </strong>
                    <span className='logs-report-share logs-muted'>
                      {hasTotal ? `${shareLabel} do total` : 'Participação indisponível'}
                    </span>
                    {hasTotal && (
                      <span className='logs-share-track' aria-hidden='true'>
                        <span style={{width: `${share}%`}} />
                      </span>
                    )}
                  </td>
                  <td className='logs-numeric-cell'>
                    <strong>{numberFormat.format(user.unique_types)}</strong>
                  </td>
                  <td className='logs-numeric-cell'>
                    <strong>{numberFormat.format(user.active_days)}</strong>
                  </td>
                  <td className='logs-report-dates'>
                    <span className='logs-report-last-date'>
                      {formatLogDate(user.last_activity)}
                    </span>
                    <small className='logs-muted logs-report-first-date'>
                      Primeira: {formatLogDate(user.first_activity)}
                    </small>
                  </td>
                  <td>
                    <div className='logs-type-chips'>
                      {topTypes.slice(0, 2).map((type) => (
                        <span
                          key={type.id_log_type}
                          className='logs-type-chip'
                          title={`${type.type}: ${numberFormat.format(type.count)} registros`}
                        >
                          <span className='logs-type-chip-label'>{type.type}</span>
                          <strong className='logs-type-chip-count'>
                            {numberFormat.format(type.count)}
                          </strong>
                        </span>
                      ))}
                      {topTypes.length > 2 && (
                        <span
                          className='logs-muted logs-more-types'
                          title={topTypes
                            .slice(2)
                            .map((type) => `${type.type}: ${numberFormat.format(type.count)}`)
                            .join('\n')}
                        >
                          +{numberFormat.format(topTypes.length - 2)} tipos
                        </span>
                      )}
                      {!topTypes.length && <span className='logs-muted'>—</span>}
                    </div>
                  </td>
                  <td className='logs-action-cell'>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary logs-drilldown-button'
                      onClick={() => onUserSelect(user.user_id)}
                      aria-label={`Ver linha do tempo de ${name}`}
                      title={`Explorar atividade de ${name}`}
                    >
                      <History size={15} aria-hidden='true' /> Linha do tempo
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
